/**
 * Emoticon → emoji auto-replacement for the message composer.
 *
 * Matching is caret-anchored and deliberately conservative: an emoticon only
 * converts when it was just completed by a single typed character AND starts
 * at a word boundary (start of text or after whitespace). That keeps URLs
 * (`https://`), times (`8:30`), brackets (`(:)`), and mid-word colons
 * (`abc:)`) untouched.
 */

// Longer emoticons must win over their suffixes (":-/" before ":/"), so
// matching walks EMOTICON_LENGTHS from longest to shortest.
// Deliberately no lowercase "xd" — it is a common literal token in Polish chat.
export const EMOTICON_MAP = {
  ":-)": "🙂",
  ":)": "🙂",
  ":-(": "🙁",
  ":(": "🙁",
  ";-)": "😉",
  ";)": "😉",
  ":-D": "😄",
  ":D": "😄",
  ":-P": "😛",
  ":-p": "😛",
  ":P": "😛",
  ":p": "😛",
  ":-O": "😮",
  ":-o": "😮",
  ":O": "😮",
  ":o": "😮",
  ":'(": "😢",
  ":|": "😐",
  ":*": "😘",
  "<3": "❤️",
  "xD": "😆",
  "XD": "😆",
  ":-/": "😕",
  ":/": "😕",
};

const EMOTICON_LENGTHS = [...new Set(Object.keys(EMOTICON_MAP).map((key) => key.length))].sort(
  (a, b) => b - a,
);

/**
 * Attempt the WhatsApp-style emoticon conversion for the change that produced
 * `nextValue` from `prevValue` with the caret now at `caretPos`.
 *
 * Only fires when exactly one character was inserted at `caretPos - 1` — a
 * paste, cut, deletion, or IME commit never converts.
 *
 * @param {string} prevValue textarea value before the change (the rendered controlled value)
 * @param {string} nextValue textarea value after the change
 * @param {number} caretPos selectionStart after the change (UTF-16 units)
 * @returns {{ value: string, caret: number, emoticon: string, emoji: string } | null}
 *   Replacement value with the caret position right after the emoji (UTF-16
 *   units, valid for setSelectionRange), or null when nothing should convert.
 */
export function applyEmoticonAutoReplace(prevValue, nextValue, caretPos) {
  if (typeof prevValue !== "string" || typeof nextValue !== "string") return null;
  if (!Number.isInteger(caretPos) || caretPos < 1 || caretPos > nextValue.length) return null;
  if (nextValue.length !== prevValue.length + 1) return null;
  // Proves the change was exactly one character inserted at caretPos - 1.
  if (nextValue.slice(0, caretPos - 1) + nextValue.slice(caretPos) !== prevValue) return null;

  for (const length of EMOTICON_LENGTHS) {
    if (caretPos < length) continue;
    const start = caretPos - length;
    const emoji = EMOTICON_MAP[nextValue.slice(start, caretPos)];
    if (!emoji) continue;
    if (start > 0 && !/\s/.test(nextValue[start - 1])) continue;
    return {
      value: nextValue.slice(0, start) + emoji + nextValue.slice(caretPos),
      caret: start + emoji.length,
      emoticon: nextValue.slice(start, caretPos),
      emoji,
    };
  }
  return null;
}
